import styled from "@emotion/styled";
import { Breakpoints, lightGreen } from "@stepfinance/react";
import type { PieSeriesOption } from "echarts";
import React from "react";

/**
 * Hacky util type to narrow down `OptionDataNumeric | OptionDataNumeric[] | PieDataItemOption` to just `PieDataItemOption`
 *
 * Not really meant to be used elsewhere
 */
type EChartsPieDataUnionHack<T> = T extends unknown[] ? never : T extends object ? T : never;

/**
 * ECharts doesn't export the type for `PieDataItemOption` so we have to infer it from the `data` prop of `PieSeriesOption`, see the hack above this definition
 */
export type PieDataItemWithKey = EChartsPieDataUnionHack<NonNullable<PieSeriesOption["data"]>[number]> & {
  key: string;
};

type PieChartLegendProps = {
  pieSeries: PieDataItemWithKey[];
  onItemClick?: (pieDatum: any) => void;
};

type PieChartLegendItemProps = {
  series: PieDataItemWithKey;
  onItemClick?: PieChartLegendProps["onItemClick"];
};

type LegendGlpyhProps = {
  size: number;
  color: string;
};

export const PieChartLegend: React.FC<PieChartLegendProps> = ({ pieSeries, onItemClick }) => {
  return (
    <LegendContainer>
      {pieSeries.map((series) => (
        <LegendItem key={series.key} series={series} onItemClick={onItemClick} />
      ))}
    </LegendContainer>
  );
};

const LegendItem: React.FC<PieChartLegendItemProps> = ({ series, onItemClick }) => {
  const { itemStyle: { color = lightGreen } = {}, name: label = "" } = series;

  return (
    <LegendItemContainer onClick={() => onItemClick?.(series)}>
      <LegendGlyph color={color as string} size={14} />
      <LegendItemSpan>{label}</LegendItemSpan>
    </LegendItemContainer>
  );
};

const LegendItemSpan = styled.span`
  white-space: nowrap;
  font-weight: 700;
  font-size: 12px;
`;

const LegendGlyph: React.FC<LegendGlpyhProps> = ({ color, size }) => {
  return (
    <svg
      style={{
        minWidth: size,
        minHeight: size,
      }}
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle r={size / 2} cx={size / 2} cy={size / 2} fill={color} />
    </svg>
  );
};

const LegendContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  max-height: 241px;

  @media (max-width: ${Breakpoints.M}px) {
    flex-direction: row;
    justify-content: center;
    gap: 5px;
  }
`;

const LegendItemContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: 300ms;

  &:hover {
    color: ${lightGreen};
  }
`;
